/* eslint-disable react/jsx-no-bind */
import { Box, Button, Grid, GridItem, Text, VStack } from "@chakra-ui/react";
import { Add as AddIcon } from "@mui/icons-material";
import { FC, useCallback, useMemo, useState } from "react";

import { SectionCard } from "~/src/components/common/SectionCard";
import { chakraFactory } from "~/src/lib/chakra-ui";

import {
  CompiledPreview,
  type CompiledPreviewLinkedModule,
} from "./CompiledPreview";
import { ModuleCreateModal } from "./ModuleCreateModal";
import { ModuleDeleteModal } from "./ModuleDeleteModal";
import { ModuleLinkModal } from "./ModuleLinkModal";
import { ModuleList } from "./ModuleList";
import { DistributionGroupOption } from "./UserTagModuleLinkFields";

const StyledSectionCard = chakraFactory(SectionCard, {
  baseStyle: {
    width: "100%",
    height: "100%",
    margin: 0,
  },
});

const StyledAddButton = chakraFactory(Button, {
  baseStyle: {
    alignSelf: "flex-end",
    marginBottom: "16px",
  },
});

type ModuleItem = {
  id: string;
  name: string;
  enabled: boolean;
  effectiveEnabled: boolean;
  priority: number;
  hasDraft: boolean;
  hasPendingToggle?: boolean;
  /**
   * Pending removal: isArchive true + deletedAt. After publish: isArchive false + deletedAt (tombstone).
   */
  isArchive?: boolean;
  deletedAt?: string | null;
  tagContent: string | null;
  draftContent: string | null;
  linkedPopupGroupNames: string[];
  linkedAbtestGroupTitles: string[];
  popupGroupIds: string[];
  abtestScenarioPageGroupIds: string[];
  /** Gating the workspace would publish, so the row sits in the matching list */
  isDistributionGated?: boolean;
  hasPendingLinkChange?: boolean;
};

type ModuleOrder = { moduleId: string; priority: number };

type Props = {
  siteId: string;
  modules: ModuleItem[];
  // Compiled preview props
  mergedTagPreview: string | null;
  draftMergedTagPreview: string | null;
  hasPendingChanges: boolean;
  hasPendingCodeChanges: boolean;
  hasPendingToggleChanges: boolean;
  enabledModuleCount: number;
  totalModuleCount: number;
  gatedModuleCount?: number;
  /** Enabled / pending 配信条件連動 modules for コンパイル済みプレビュー Tab 2. */
  linkedModules?: CompiledPreviewLinkedModule[];
  // Handlers
  onModuleToggle: (moduleId: string, enabled: boolean) => void;
  /** Opens the combined module editor (metadata + code). */
  onOpenModuleEditor: (module: ModuleItem) => void;
  onModuleDelete: (moduleId: string) => void;
  onModuleReorder: (
    moduleOrders: { moduleId: string; priority: number }[]
  ) => void;
  onModuleCreate: (
    name: string,
    content: string,
    description: string | undefined
  ) => Promise<void>;
  onModuleLink: (
    moduleId: string,
    popupGroupIds: string[],
    abtestScenarioPageGroupIds: string[]
  ) => Promise<void>;
  popupGroupOptions: DistributionGroupOption[];
  abtestGroupOptions: DistributionGroupOption[];
  onCancelCompiledChanges?: () => void;
  onPublishCompiledChanges?: () => void;
  isPublishCompiledDisabled?: boolean;
};

const sortByPriority = (a: ModuleItem, b: ModuleItem) =>
  a.priority - b.priority;

export const ModuleListTab: FC<Props> = ({
  siteId: _siteId,
  modules,
  mergedTagPreview,
  draftMergedTagPreview,
  hasPendingChanges,
  hasPendingCodeChanges,
  hasPendingToggleChanges,
  enabledModuleCount,
  totalModuleCount,
  gatedModuleCount = 0,
  linkedModules,
  onModuleToggle,
  onOpenModuleEditor,
  onModuleDelete,
  onModuleReorder,
  onModuleCreate,
  onModuleLink,
  popupGroupOptions,
  abtestGroupOptions,
  onCancelCompiledChanges,
  onPublishCompiledChanges,
  isPublishCompiledDisabled = false,
}) => {
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [linkingModule, setLinkingModule] = useState<ModuleItem | null>(null);
  const [isLinkSaving, setIsLinkSaving] = useState(false);
  const [deletingModule, setDeletingModule] = useState<{
    id: string;
    name: string;
  } | null>(null);

  const sdkModules = useMemo(
    () => modules.filter((m) => !m.isDistributionGated).sort(sortByPriority),
    [modules]
  );

  const linkedModuleList = useMemo(
    () => modules.filter((m) => !!m.isDistributionGated).sort(sortByPriority),
    [modules]
  );

  // (active/total): active = switch on (effectiveEnabled); excludes staged/tombstone rows.
  const moduleCountLabel = useCallback((list: ModuleItem[]) => {
    const live = list.filter((m) => !m.deletedAt && !m.isArchive);
    const active = live.filter((m) => m.effectiveEnabled).length;
    return `(${active}/${live.length})`;
  }, []);

  const buildMergedOrders = useCallback(
    (reorderedIds: string[], otherIdsInOrder: string[]): ModuleOrder[] =>
      [...reorderedIds, ...otherIdsInOrder].map((moduleId, priority) => ({
        moduleId,
        priority,
      })),
    []
  );

  const handleSdkReorder = useCallback(
    (moduleOrders: ModuleOrder[]) => {
      const sdkOrderedIds = [...moduleOrders]
        .sort((a, b) => a.priority - b.priority)
        .map((o) => o.moduleId);
      const linkedOrderedIds = linkedModuleList.map((m) => m.id);
      onModuleReorder(buildMergedOrders(sdkOrderedIds, linkedOrderedIds));
    },
    [buildMergedOrders, linkedModuleList, onModuleReorder]
  );

  const handleLinkedReorder = useCallback(
    (moduleOrders: ModuleOrder[]) => {
      const linkedOrderedIds = [...moduleOrders]
        .sort((a, b) => a.priority - b.priority)
        .map((o) => o.moduleId);
      const sdkOrderedIds = sdkModules.map((m) => m.id);
      onModuleReorder(buildMergedOrders(sdkOrderedIds, linkedOrderedIds));
    },
    [buildMergedOrders, onModuleReorder, sdkModules]
  );

  const handleDeleteModule = (moduleId: string) => {
    const module = modules.find((m) => m.id === moduleId);
    if (module) {
      setDeletingModule({ id: module.id, name: module.name });
    }
  };

  const handleDeleteConfirm = () => {
    if (deletingModule) {
      onModuleDelete(deletingModule.id);
      setDeletingModule(null);
    }
  };

  const handleOpenLinkModal = useCallback((module: ModuleItem) => {
    setLinkingModule(module);
  }, []);

  const handleLinkSubmit = useCallback(
    async (popupGroupIds: string[], abtestScenarioPageGroupIds: string[]) => {
      if (!linkingModule) return;

      setIsLinkSaving(true);
      try {
        await onModuleLink(
          linkingModule.id,
          popupGroupIds,
          abtestScenarioPageGroupIds
        );
        setLinkingModule(null);
      } finally {
        setIsLinkSaving(false);
      }
    },
    [linkingModule, onModuleLink]
  );

  return (
    <Box width="100%" height="750px" minW={0} overflow="hidden">
      <Grid templateColumns="1fr 1fr" gap="24px" height="100%" minW={0}>
        <GridItem minW={0} overflow="hidden">
          <StyledSectionCard>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              mb="16px"
            >
              <Box />
              <StyledAddButton
                leftIcon={<AddIcon />}
                colorScheme="blue"
                size="sm"
                onClick={() => setIsCreateModalOpen(true)}
              >
                モジュール追加
              </StyledAddButton>
            </Box>

            <Box maxHeight="600px" overflowY="auto">
              <VStack spacing="20px" align="stretch">
                <Box>
                  <Text
                    fontSize="sm"
                    fontWeight="semibold"
                    color="gray.700"
                    mb="8px"
                  >
                    SDKモジュール {moduleCountLabel(sdkModules)}
                  </Text>
                  <Text fontSize="xs" color="gray.500" mb="12px">
                    CDNにマージされるモジュールです。ポップアップ/ABテストにリンクすると下のリストへ移動し、「変更を公開」で配信に反映されます。
                  </Text>
                  <ModuleList
                    modules={sdkModules}
                    onToggle={onModuleToggle}
                    onEdit={onOpenModuleEditor}
                    onLink={handleOpenLinkModal}
                    onDelete={handleDeleteModule}
                    onReorder={handleSdkReorder}
                    emptyTitle="SDKモジュールがありません"
                    emptyDescription="「モジュール追加」から作成するか、配信条件連動のリンクを解除してください"
                  />
                </Box>

                <Box>
                  <Text
                    fontSize="sm"
                    fontWeight="semibold"
                    color="gray.700"
                    mb="8px"
                  >
                    配信条件連動モジュール {moduleCountLabel(linkedModuleList)}
                  </Text>
                  <Text fontSize="xs" color="gray.500" mb="12px">
                    ポップアップ/ABテストにリンクされたモジュールです。CDNマージ対象外で、配信条件を満たしたときのみ実行されます。リンクの解除は「変更を公開」までCDNへ反映されません。
                  </Text>
                  <ModuleList
                    modules={linkedModuleList}
                    onToggle={onModuleToggle}
                    onEdit={onOpenModuleEditor}
                    onLink={handleOpenLinkModal}
                    onDelete={handleDeleteModule}
                    onReorder={handleLinkedReorder}
                    emptyTitle="配信条件連動モジュールがありません"
                    emptyDescription="リンクアイコンからポップアップまたはABテストグループを紐づけると、こちらに表示されます"
                  />
                </Box>
              </VStack>
            </Box>
          </StyledSectionCard>
        </GridItem>

        <GridItem height="100%" minW={0} overflow="hidden">
          <CompiledPreview
            mergedTagPreview={mergedTagPreview}
            draftMergedTagPreview={draftMergedTagPreview}
            hasPendingChanges={hasPendingChanges}
            hasPendingCodeChanges={hasPendingCodeChanges}
            hasPendingToggleChanges={hasPendingToggleChanges}
            enabledModuleCount={enabledModuleCount}
            totalModuleCount={totalModuleCount}
            gatedModuleCount={gatedModuleCount}
            linkedModules={linkedModules}
            onCancelChanges={onCancelCompiledChanges}
            onPublishChanges={onPublishCompiledChanges}
            isPublishDisabled={isPublishCompiledDisabled}
          />
        </GridItem>
      </Grid>

      <ModuleCreateModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSubmit={onModuleCreate}
      />

      <ModuleLinkModal
        isOpen={!!linkingModule}
        moduleName={linkingModule?.name ?? ""}
        onClose={() => setLinkingModule(null)}
        onSubmit={handleLinkSubmit}
        popupGroupOptions={popupGroupOptions}
        abtestGroupOptions={abtestGroupOptions}
        initialPopupGroupIds={linkingModule?.popupGroupIds ?? []}
        initialAbtestScenarioPageGroupIds={
          linkingModule?.abtestScenarioPageGroupIds ?? []
        }
        isLoading={isLinkSaving}
      />

      <ModuleDeleteModal
        isOpen={!!deletingModule}
        onClose={() => setDeletingModule(null)}
        moduleName={deletingModule?.name || ""}
        onConfirm={handleDeleteConfirm}
      />
    </Box>
  );
};
